import type { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Container,
  type ContainerProps,
  Flex,
  GradientText,
  Grid,
  Icon,
  Separate,
  Spacing,
  Text,
} from "@huub-web/ui";

import type {
  AnalysisObservation as AnalysisObservationType,
  AnalysisObservationCategory,
} from "../../../types";
import { AnalysisObservation } from "./AnalysisObservation";

import {
  descriptionStyle,
  observationsGridStyle,
  plusHeadingStyle,
} from "./AnalysisObservationsSlide.css";

/**
 * @deprecated
 */
export type AnalysisObservationsSlideProps = {
  category: AnalysisObservationCategory;
  container: ContainerProps["size"];
  onShowAll: () => void;
  onObservationClick: (observation: AnalysisObservationType) => void;
};

/**
 * @deprecated
 */
export const AnalysisObservationsSlide: FC<AnalysisObservationsSlideProps> = ({
  category,
  container,
  onShowAll,
  onObservationClick,
}) => {
  const { t } = useTranslation();
  const observations = category?.observations?.slice(0, 6) ?? [];

  const handleObservationClick = (observation: AnalysisObservationType) => {
    onObservationClick(observation);
  };

  return (
    <>
      <Spacing height="xs" />
      <Container size={container}>
        <Text
          variant="bodyL"
          align="center"
          className={descriptionStyle}
          markdown
        >
          {category.description}
        </Text>

        <Grid variant="none" className={observationsGridStyle}>
          {observations.map((observation, index) => (
            <AnalysisObservation
              key={index}
              observation={observation}
              onClick={() => handleObservationClick(observation)}
            />
          ))}
        </Grid>

        {observations.length < observations?.length && (
          <Button
            onClick={onShowAll}
            alignSelf="center"
            variant="opaque"
            border="default"
          >
            {t("analysisObservations.viewAllInsights")}
          </Button>
        )}

        {!!category.plusPoints && category.plusPoints?.length > 0 && (
          <>
            <Spacing height="xs" />
            <Grid variant="twoColumn">
              <Flex>
                <Text variant="displayL" className={plusHeadingStyle} bold>
                  <GradientText>
                    {t("analysisObservations.plusPoints")}
                  </GradientText>{" "}
                  {category.name.toLowerCase()}
                </Text>
              </Flex>

              <Flex as="ul" direction="column" gap="m">
                <Separate>
                  {category.plusPoints.map((plusPoint) => (
                    <Flex as="li" key={plusPoint.label} align="center" gap="s">
                      <Icon icon="checkCircleFilled" />
                      <Text grow>{plusPoint.label}</Text>
                    </Flex>
                  ))}
                </Separate>
              </Flex>
            </Grid>
          </>
        )}
      </Container>
    </>
  );
};
